import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {(() => void) | null} */
  #deferCleanup = null;

  /** @type {boolean} */
  #deferAborted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#deferAborted = false;

    if (this.hasAttribute('lazy')) {
      this.#observeVisibility();
      return;
    }

    if (this.hasAttribute('defer')) {
      this.#deferLoadVideo();
      return;
    }

    this.loadVideo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#deferAborted = true;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#deferCleanup?.();
    this.#deferCleanup = null;
  }

  loadVideo() {
    if (this.hasAttribute('data-video-loaded')) return;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    this.setAttribute('data-video-loaded', '');
  }

  #observeVisibility() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        this.loadVideo();
        this.#intersectionObserver?.disconnect();
        this.#intersectionObserver = null;
      },
      { rootMargin: '200px' }
    );

    this.#intersectionObserver.observe(this);
  }

  #deferLoadVideo() {
    const poster = this.querySelector('.video-background__poster');

    const scheduleLoad = () => {
      requestIdleCallback(() => {
        if (this.#deferAborted || !this.isConnected) return;

        this.loadVideo();
      });
    };

    if (!(poster instanceof HTMLImageElement) || poster.complete) {
      scheduleLoad();
      return;
    }

    const onPosterReady = () => {
      poster.removeEventListener('load', onPosterReady);
      poster.removeEventListener('error', onPosterReady);
      this.#deferCleanup = null;

      if (this.#deferAborted) return;

      scheduleLoad();
    };

    poster.addEventListener('load', onPosterReady);
    poster.addEventListener('error', onPosterReady);
    this.#deferCleanup = () => {
      poster.removeEventListener('load', onPosterReady);
      poster.removeEventListener('error', onPosterReady);
    };
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
